import { useCallback } from 'react';
import { useReactiveVar } from '@apollo/client';
import { TFunction } from 'i18next';

import {
  useLinkStaffMailboxMutation,
  useStaffMailboxDomainsQuery,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * ТЗ 0083 — рабочая почта Google Workspace сотрудника.
 *
 * Привязывает только Admin (решение владельца 2026-09-23): ERP читает ящик
 * через доступ ко всему домену, и указать адрес = получить доступ к его
 * письмам в ERP. Скрытое поле у не-админа — UX; отказ даёт сервер.
 */
/**
 * «Мой профиль» — всем, кто входит в ERP (зеркало `ERP_LOGIN_ROLES`
 * бэкенда): там своя рабочая почта и правила переписки.
 */
export const PROFILE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
  UserRole.FINANCIER,
  UserRole.METHODIST,
  // ТЗ 0088: Marketing входит в `ERP_LOGIN_ROLES` (но не в MANAGEMENT_ROLES).
  UserRole.MARKETING,
];

export const useIsErpAdmin = (): boolean => {
  const { me } = useReactiveVar(userStateVar);
  return ((me?.roles || []) as UserRole[]).includes(UserRole.ADMIN);
};

/**
 * Один и тот же пустой список: результат хука стоит в зависимостях эффектов
 * (`useCreateNewUser` диспатчит ошибки по нему), и новый `[]` на каждый
 * рендер крутил эффект бесконечно, пока домены не загружены или пропущены.
 */
const NO_DOMAINS: string[] = [];

/** Рабочие домены: подсказка адреса и текст ошибки «не тот домен». */
export const useWorkspaceDomains = (skip = false): string[] => {
  const { data } = useStaffMailboxDomainsQuery({
    skip,
    fetchPolicy: 'cache-first',
  });
  return data?.staffMailboxSettings.domains || NO_DOMAINS;
};

/**
 * Логин сотрудника как подсказка рабочего адреса — только если он из
 * рабочего домена. Доменов не задано — не подсказываем: любой адрес был бы
 * догадкой.
 */
export const suggestWorkspaceEmail = (
  login: string,
  domains: string[],
): string => {
  const email = (login || '').trim().toLowerCase();
  const domain = email.includes('@') ? email.split('@').pop() || '' : '';
  return domain && domains.includes(domain) ? email : '';
};

const KNOWN_ERRORS = [
  'INVALID_EMAIL',
  'WRONG_DOMAIN',
  'MAILBOX_SHARED',
  'MAILBOX_TAKEN',
  'FORBIDDEN',
];

/** Ошибка привязки по коду — на языке интерфейса; иначе текст сервера. */
export const staffMailboxErrorText = (
  t: TFunction,
  code: string | null | undefined,
  message: string | null | undefined,
  domains: string[],
): string => {
  if (code && KNOWN_ERRORS.includes(code)) {
    return `${t(`staffMailbox.errors.${code}`, {
      domains: domains.join(', '),
    })}`;
  }
  return message || `${t('common.somethingWentWrong')}`;
};

export interface ILinkStaffMailboxResult {
  ok: boolean;
  code?: string | null;
  message?: string | null;
}

/** Привязать (или отвязать — пустой адрес) рабочую почту сотрудника. */
export const useLinkStaffMailbox = (): [
  (userId: string, email: string) => Promise<ILinkStaffMailboxResult>,
  { loading: boolean },
] => {
  const [mutate, { loading }] = useLinkStaffMailboxMutation();
  const link = useCallback(
    async (userId: string, email: string) => {
      try {
        const { data } = await mutate({
          variables: { userId, email: email.trim() },
          // Колонка «Рабочая почта» списка сотрудников — окно открыто над
          // ним, запрос активен. «Мой профиль» перечитает себя сам при входе.
          refetchQueries: ['PaginatedUsersSettings'],
        });
        const error = data?.setStaffMailbox?.customErrors?.[0];
        if (error || !data?.setStaffMailbox?.success) {
          return { ok: false, code: error?.code, message: error?.message };
        }
        return { ok: true };
      } catch (e) {
        return { ok: false, message: (e as Error).message };
      }
    },
    [mutate],
  );
  return [link, { loading }];
};
